import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import logoHorizontal from '@/assets/logo-horizontal-bk.svg';
import { getErrorMessage } from '@/lib/errors';

const inputCls = 'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

export function OlvidePasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.post('/api/auth/forgot-password', { email }).then(r => r.data),
    onSuccess: () => setSent(true),
    onError: (e: any) => toast.error(getErrorMessage(e, 'No se pudo procesar la solicitud')),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return toast.error('Ingresa tu correo electrónico');
    mutation.mutate();
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={logoHorizontal} alt="Anik" className="h-10 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Recuperar contraseña</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {sent ? (
            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <MailCheck className="text-emerald-500" size={40} />
              <p className="font-semibold text-gray-800">Revisa tu correo</p>
              <p className="text-sm text-gray-500">
                Si existe una cuenta con el correo <strong>{email}</strong>, te enviamos un enlace para restablecer tu contraseña.
                El enlace es válido por 1 hora.
              </p>
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                Volver al inicio de sesión
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <p className="text-sm text-gray-500">
                Ingresa el correo electrónico asociado a tu cuenta y te enviaremos un enlace para restablecer tu contraseña.
              </p>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Correo electrónico</label>
                <input
                  className={inputCls}
                  type="email"
                  placeholder="tu@correo.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={mutation.isPending}
                className="flex items-center justify-center gap-2 w-full py-3 bg-primary-600 hover:bg-primary-500 disabled:opacity-50 text-white font-semibold text-sm rounded-xl transition-colors"
              >
                {mutation.isPending && <Loader2 size={15} className="animate-spin" />}
                Enviar enlace de recuperación
              </button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="flex items-center justify-center gap-1.5 w-full text-xs text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft size={14} />
                Volver al inicio de sesión
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
